import React, { createContext, useEffect, useState, useRef } from "react";
import {
  PublicClientApplication,
  AuthenticationResult,
  AccountInfo,
  InteractionRequiredAuthError
} from "@azure/msal-browser";
import Loader from "components/Loader";
import appConfig from "appConfig";
import axios, { AxiosResponse } from "axios";
import type { ApiResponse } from "api/api";
import { appInsights, setAppInsightsUser, clearAppInsightsUser } from "utils/appInsights";

const isNative = typeof window.nativeTokenProvider === "function";

// Helper to check if debug logging is enabled
const isDebugEnabled = () => typeof window !== 'undefined' && (window as any)._debugSignalR === true;

type EntraIdContextType = {
  isAuthenticated: boolean;
  user: AccountInfo | null;
  accessToken: string | null;
  getCurrentToken: () => string | null;
  login: () => Promise<void>;
  logout: () => void;
  refreshToken: () => Promise<string | null>;
};

const EntraIdContext = createContext<EntraIdContextType | null>(null);

export const EntraIdProvider: React.FC<{
  children: React.ReactNode;
  msalInstance: PublicClientApplication;
}> = ({ children, msalInstance }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<AccountInfo | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const keepAliveStarted = useRef(false);
  const keepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastInOfficeStatus = useRef<boolean | null>(null); // Track last known in-office status



  // Helper function to decode JWT and get expiration
  const decodeJwtExpiration = (token: string): Date | null => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      return new Date(payload.exp * 1000);
    } catch (error) {
      console.error('Failed to decode JWT:', error);
      return null;
    }
  };

  async function acquireToken(scope: string): Promise<string> {
    if (isNative) {
      return window.nativeTokenProvider!(scope);
    } else {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) throw new Error("No MSAL account");
      const result = await msalInstance.acquireTokenSilent({
        account: accounts[0],
        scopes: [scope],
      });
      return result.accessToken;
    }
  }

  // Whenever accessToken state changes, also update the ref
  useEffect(() => {
    tokenRef.current = accessToken;
  }, [accessToken]);

  function getCurrentToken() {
    // Always the latest token from the ref
    return tokenRef.current;
  }

  // 1. A helper to check if user is in the office
  async function checkIfUserInOffice() : Promise<boolean> {
    try {
      let token = getCurrentToken();
      const response = await axios.get<ApiResponse<boolean>>(
        `${appConfig.api.orderApiUrl}/lookups/isuserinoffice`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      const inOffice = response.data.data === true;
      
      // Only log to App Insights if this is the first check or status changed
      const isFirstCheck = lastInOfficeStatus.current === null;
      const statusChanged = !isFirstCheck && lastInOfficeStatus.current !== inOffice;
      
      if (isFirstCheck || statusChanged) {
        appInsights?.trackEvent({
          name: 'UserInOfficeCheck',
          properties: {
            inOffice: inOffice,
            previousStatus: lastInOfficeStatus.current?.toString() ?? 'none',
            changeType: isFirstCheck ? 'initial' : 'statusChanged',
            timestamp: new Date().toISOString(),
            context: 'checkIfUserInOffice'
          }
        });
      }
      
      // Update the last known status
      lastInOfficeStatus.current = inOffice;
      
      return inOffice;
    } catch (err) {
      console.error("Failed to check if user is in office:", err);
      
      // Track failure to Application Insights (always log errors)
      appInsights?.trackException({
        exception: err instanceof Error ? err : new Error(String(err)),
        properties: {
          context: 'checkIfUserInOffice_Failed',
          lastKnownStatus: lastInOfficeStatus.current?.toString() ?? 'unknown',
          timestamp: new Date().toISOString()
        }
      });
      
      return false;
    }
  }

  // 2. Start keep-alive loop if user is in the office
  function startKeepAlive() {
    checkIfUserInOffice().then((inOffice) => {
      if (isDebugEnabled()) {
        console.log(">>> Keep-alive: checking if user is in office", { inOffice });
      }
      
      if (!inOffice) {
        if (isDebugEnabled()) {
          console.log(">>> Keep-alive: user not in office, skipping");
        }
        return;
      }

      if (isDebugEnabled()) {
        console.log(">>> Keep-alive: setting up interval (15 minutes)", {
          intervalMs: 15 * 60 * 1000,
          nextRefresh: new Date(Date.now() + 15 * 60 * 1000).toLocaleTimeString()
        });
      }

      // Store interval in ref to prevent garbage collection
      keepAliveIntervalRef.current = setInterval(async () => {
        if (isDebugEnabled()) {
          console.log(">>> Keep-alive: interval fired!", { 
            timestamp: new Date().toISOString() 
          });
        }
        
        try {
          const stillInOffice = await checkIfUserInOffice();
          if (isDebugEnabled()) {
            console.log(">>> Keep-alive: checking if still in office", { stillInOffice });
          }
        
        if (!stillInOffice) {
          if (isDebugEnabled()) {
            console.log(">>> Keep-alive: user no longer in office, stopping");
          }
          if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
          }
          return;
        }

        if(isNative) {
          try {
            const accessToken = await acquireToken(`api://${appConfig.entraID.authServerName}/${appConfig.entraID.userImpersonationScope}`);
            if (isDebugEnabled()) {
              console.log(">>> Keep-alive: native token refreshed", { newToken: `${accessToken.substring(0, 20)}...` });
            }
            setAccessToken(accessToken);
            setIsAuthenticated(true);
            
            appInsights?.trackEvent({
              name: 'TokenRefresh_KeepAlive_Success',
              properties: {
                type: 'native',
                inOffice: stillInOffice,
                timestamp: new Date().toISOString()
              }
            });
          } catch (error) {
            console.error(">>> Keep-alive: native token refresh failed", error);
            
            appInsights?.trackException({
              exception: error instanceof Error ? error : new Error(String(error)),
              properties: {
                context: 'KeepAlive_Native',
                timestamp: new Date().toISOString()
              }
            });
          }
          return;
        }

        // user is still in office, so refresh token
        try {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            const account = accounts[0];
            
            try {
              const tokenResponse: AuthenticationResult = await msalInstance.acquireTokenSilent({
                account,
                scopes: [
                  `api://${appConfig.entraID.authServerName}/${appConfig.entraID.userImpersonationScope}`,
                  "User.Read"
                ],
                forceRefresh: true
              });
              
              const timeUntilExpiry = tokenResponse.expiresOn ? 
                Math.round((tokenResponse.expiresOn.getTime() - Date.now()) / 1000 / 60) : null;
              
              if (isDebugEnabled()) {
                console.log(">>> Keep-alive: Token refreshed", {
                  success: true,
                  isNewToken: tokenResponse.accessToken !== tokenRef.current,
                  oldExpiry: tokenRef.current ? decodeJwtExpiration(tokenRef.current) : null,
                  newExpiry: decodeJwtExpiration(tokenResponse.accessToken),                
                  timeUntilExpiry: timeUntilExpiry ? `${timeUntilExpiry} minutes` : 'unknown'
                });
              }
              
              setAccessToken(tokenResponse.accessToken);
              
              appInsights?.trackEvent({
                name: 'TokenRefresh_KeepAlive_Success',
                properties: {
                  type: 'msal',
                  timeUntilExpiry: timeUntilExpiry?.toString(),
                  inOffice: stillInOffice,
                  timestamp: new Date().toISOString()
                }
              });
            } catch (error) {
              console.error(">>> Keep-alive: Token refresh failed", {
                error: error instanceof Error ? error.message : String(error),
                timestamp: new Date().toISOString()
              });
              
              appInsights?.trackException({
                exception: error instanceof Error ? error : new Error(String(error)),
                properties: {
                  context: 'KeepAlive_MSAL',
                  timestamp: new Date().toISOString()
                }
              });
            }
          }
        } catch (err) {
          console.error(">>> Keep-alive: failed to refresh token", err);
          if (keepAliveIntervalRef.current) {
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
          }
        }
        } catch (error) {
          console.error(">>> Keep-alive: interval callback error", error);
        }
      }, 15 * 60 * 1000); // every 15 minutes

      if (isDebugEnabled()) {
        console.log(">>> Keep-alive: started successfully");
      }
    }).catch((error) => {
      console.error(">>> Keep-alive: failed to start", error);
    });
  }

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        if(isNative) {
          const accessToken = await acquireToken(`api://${appConfig.entraID.authServerName}/${appConfig.entraID.userImpersonationScope}`);
          setAccessToken(accessToken);
          //setUser(response.account);
          setIsAuthenticated(true);          
          setIsInitialized(true);
          return; // We have our token; no need to check further
        }

        // 1) Handle the redirect response first
        const response = await msalInstance.handleRedirectPromise();
        if (response && response.account) {
          setUser(response.account);
          setIsAuthenticated(true);
          setAccessToken(response.accessToken);

          // Store user email (username) in localStorage for next loginHint
          localStorage.setItem("msalUserEmail", response.account.username);

          setIsInitialized(true);
          return; // We have our token; no need to check further
        }

        // 2) If no new redirect, check existing accounts
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          // We have a cached account
          const account = accounts[0];
          setUser(account);
          setIsAuthenticated(true);

          // Try to get a fresh token silently
          try {
            const tokenResponse: AuthenticationResult =
              await msalInstance.acquireTokenSilent({
                account,
                scopes: [
                  `api://${appConfig.entraID.authServerName}/${appConfig.entraID.userImpersonationScope}`,
                  "User.Read"
                ]
              });
            setAccessToken(tokenResponse.accessToken);
          } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
              console.warn("Silent token acquisition failed, user might need to log in again.");
            } else {
              console.error("Silent token acquisition failed:", error);
            }
          }
        } else {
          // 3) No cached accounts: see if we have a stored email
          const storedEmail = localStorage.getItem("msalUserEmail");
          if (storedEmail) {
            // Attempt an immediate silent (or redirect) login with that hint
            console.log("No accounts in cache, but found stored email. Attempting login with hint:", storedEmail);

            try {
              // We'll do a silent login attempt first
              // If user already has an active AAD session, this might succeed
              const tokenResponse: AuthenticationResult =
                await msalInstance.ssoSilent({
                  scopes: [
                    `api://${appConfig.entraID.authServerName}/${appConfig.entraID.userImpersonationScope}`,
                    "User.Read"
                  ],
                  loginHint: storedEmail
                });

              console.log("ssoSilent success with storedEmail", tokenResponse.account);
              setUser(tokenResponse.account || null);
              setIsAuthenticated(true);
              setAccessToken(tokenResponse.accessToken);

              // Also store that email for future
              if (tokenResponse.account?.username) {
                localStorage.setItem("msalUserEmail", tokenResponse.account.username);
              }
            } catch (error) {
              if (error instanceof InteractionRequiredAuthError) {
                console.warn("SSO silent failed, fallback to redirect with loginHint:", storedEmail);
                // If silent fails, fallback to a normal redirect-based login
                msalInstance.loginRedirect({
                  scopes: [
                    `api://${appConfig.entraID.authServerName}/${appConfig.entraID.userImpersonationScope}`,
                    "User.Read"
                  ],
                  loginHint: storedEmail
                });
                return; // We'll handle the response next time we land here
              } else {
                console.error("ssoSilent error:", error);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error initializing authentication:", error);
      } finally {
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, [msalInstance]);

  useEffect(() => {
    // Only call startKeepAlive() once
    if (isAuthenticated && accessToken && !keepAliveStarted.current) {
      keepAliveStarted.current = true;
      startKeepAlive();
    }
  }, [isAuthenticated, accessToken]);

  // Separate cleanup effect that only runs on component unmount
  useEffect(() => {
    return () => {
      if (keepAliveIntervalRef.current) {
        if (isDebugEnabled()) {
          console.log(">>> Keep-alive: cleaning up interval on component unmount");
        }
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
      }
    };
  }, []); // Empty dependency array = only runs on mount/unmount

  // Update Application Insights user context when authentication state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      // Extract user info from account
      const userName = user.username || user.name || null;
      const userEmail = user.username || null; // username is typically the email
      const userId = user.localAccountId || user.homeAccountId || undefined;
      
      console.log('[AppInsights] Setting user context:', { userName, userEmail, userId });
      setAppInsightsUser(userName, userEmail, userId);
    } else {
      console.log('[AppInsights] Clearing user context');
      clearAppInsightsUser();
    }
  }, [isAuthenticated, user]);

  const login = async () => {
    try {
      const storedEmail = localStorage.getItem("msalUserEmail") || undefined;
      await msalInstance.loginRedirect({
        scopes: [
          `api://${appConfig.entraID.authServerName}/${appConfig.entraID.userImpersonationScope}`,
          "User.Read"
        ],
        loginHint: storedEmail
      });
    } catch (error) {
      console.error("Login failed", error);
    }
  };

  const logout = () => {
    msalInstance.logoutRedirect().then(() => {
      setUser(null);
      setIsAuthenticated(false);
      setAccessToken(null);
    });
  };

  // DEBUG: Function to force token expiration for testing
  const expireTokenForTesting = () => {
    console.warn('🧪 DEBUG: Forcing token expiration for testing');
    setAccessToken(null);
    tokenRef.current = null;
  };

  // Expose debug functions globally under window.debug namespace (only initialize once)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Initialize internal flags only if they don't exist
      if ((window as any)._forceRefreshFailure === undefined) {
        (window as any)._forceRefreshFailure = false;
      }
      if ((window as any)._forceInteractionRequired === undefined) {
        (window as any)._forceInteractionRequired = false;
      }
      if ((window as any)._debugSignalR === undefined) {
        (window as any)._debugSignalR = false;
      }
      
      // Create debug namespace if it doesn't exist
      if (!(window as any).debug) {
        (window as any).debug = {};
      }
      
      // Token testing functions
      (window as any).debug.expireToken = () => {
        console.warn('🧪 DEBUG: Forcing token expiration for testing');
        expireTokenForTesting();
      };
      
      (window as any).debug.forceRefreshFailure = () => {
        console.warn('🧪 DEBUG: Next token refresh will fail');
        (window as any)._forceRefreshFailure = true;
      };
      
      (window as any).debug.forceInteractionRequired = () => {
        console.warn('🧪 DEBUG: Next token refresh will require user interaction');
        (window as any)._forceInteractionRequired = true;
      };
      
      // Locale debugging function - shows current locale info immediately
      (window as any).debug.showLocaleInfo = () => {
        const systemLocale = Intl.DateTimeFormat().resolvedOptions().locale;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const browserLang = navigator.language;
        const browserLangs = navigator.languages;
        
        const isUKTimezone = timezone.includes('London') || timezone.includes('Europe/London');
        const isUSTimezone = timezone.includes('New_York') || timezone.includes('America/New_York');
        
        console.group('🌍 Locale Information');
        console.log('System Locale:', systemLocale);
        console.log('Timezone:', timezone);
        console.log('Browser Language:', browserLang);
        console.log('Browser Languages:', browserLangs);
        console.log('Is UK Timezone:', isUKTimezone);
        console.log('Is US Timezone:', isUSTimezone);
        
        // Show override logic
        if (systemLocale === 'en-US' && isUKTimezone) {
          console.warn('⚠️  Override Applied: en-US → en-GB (based on timezone)');
          console.log('Final Locale Used:', 'en-GB');
        } else if (systemLocale === 'en-GB' && isUSTimezone) {
          console.warn('⚠️  Override Applied: en-GB → en-US (based on timezone)');
          console.log('Final Locale Used:', 'en-US');
        } else {
          console.log('✅ Final Locale Used:', systemLocale, '(no override needed)');
        }
        console.groupEnd();
      };
      
      // SignalR and Keep-alive debugging functions
      (window as any).debug.enableSignalR = () => {
        console.log('🧪 DEBUG: SignalR & keep-alive verbose logging enabled');
        (window as any)._debugSignalR = true;
      };
      
      (window as any).debug.disableSignalR = () => {
        console.log('🧪 DEBUG: SignalR & keep-alive verbose logging disabled');
        (window as any)._debugSignalR = false;
      };
      
      (window as any).debug.clearOwnershipCache = () => {
        let cleared = 0;
        
        // Clear ownership caches from localStorage without touching MSAL data
        const localKeys = Object.keys(localStorage);
        localKeys.forEach(key => {
          if (key.includes('owned_orders_cache') || key.includes('ignored_orders_cache')) {
            localStorage.removeItem(key);
            cleared++;
          }
        });
        
        // Also clear SignalR messages from sessionStorage
        const sessionKeys = Object.keys(sessionStorage);
        sessionKeys.forEach(key => {
          if (key.includes('signalr_messages')) {
            sessionStorage.removeItem(key);
            cleared++;
          }
        });
        
        console.log(`🧪 DEBUG: Cleared ${cleared} ownership/message cache entries (MSAL data preserved)`);
      };
      
      // Reset all debug flags
      (window as any).debug.resetFlags = () => {
        console.log('🧪 DEBUG: Resetting all debug flags');
        (window as any)._forceRefreshFailure = false;
        (window as any)._forceInteractionRequired = false;
        (window as any)._debugSignalR = false;
      };
      
      // Help function to list all available debug commands
      (window as any).debug.help = () => {
        console.group('🧪 Available Debug Commands');
        console.log('%c🔐 Token Testing:', 'font-weight: bold; color: #0984e3;');
        console.log('  %cdebug.expireToken()', 'font-weight: bold', '- Expire the current token to test refresh flow');
        console.log('  %cdebug.forceRefreshFailure()', 'font-weight: bold', '- Next token refresh will fail (then call expireToken)');
        console.log('  %cdebug.forceInteractionRequired()', 'font-weight: bold', '- Next token refresh will require user interaction');
        console.log('');
        console.log('%c🌍 Locale:', 'font-weight: bold; color: #00b894;');
        console.log('  %cdebug.showLocaleInfo()', 'font-weight: bold', '- Show current locale detection information');
        console.log('');
        console.log('%c📡 SignalR & Token Refresh:', 'font-weight: bold; color: #fdcb6e;');
        console.log('  %cdebug.enableSignalR()', 'font-weight: bold', '- Enable verbose SignalR & keep-alive logging');
        console.log('  %cdebug.disableSignalR()', 'font-weight: bold', '- Disable verbose SignalR & keep-alive logging');
        console.log('  %cdebug.clearOwnershipCache()', 'font-weight: bold', '- Clear order ownership cache (preserves MSAL)');
        console.log('  %c(Connection events and errors always shown)', 'font-style: italic; color: #999;');
        console.log('');
        console.log('%c🔄 Version Control:', 'font-weight: bold; color: #e17055;');
        console.log('  %cdebug.version.simulate("999.999.999")', 'font-weight: bold', '- Simulate a new version for testing');
        console.log('  %cdebug.version.forceCheck()', 'font-weight: bold', '- Force immediate version check (triggers UI)');
        console.log('  %cdebug.version.getCurrentInfo()', 'font-weight: bold', '- Get current version information');
        console.log('  %cdebug.version.setInterval(30)', 'font-weight: bold', '- Set polling interval (seconds) for debugging');
        console.log('  %cdebug.version.getInterval()', 'font-weight: bold', '- Show current polling configuration');
        console.log('  %cdebug.version.resetInterval()', 'font-weight: bold', '- Reset to default polling interval');
        console.log('  %cdebug.version.enableVerbose()', 'font-weight: bold', '- Enable detailed version check logs');
        console.log('  %cdebug.version.disableVerbose()', 'font-weight: bold', '- Disable detailed logs');
        console.log('  %cdebug.version.clearSimulation()', 'font-weight: bold', '- Clear simulated version');
        console.log('  %cdebug.version.clearCaches()', 'font-weight: bold', '- Clear all browser caches');
        console.log('');
        console.log('%c📊 Version Analytics Testing:', 'font-weight: bold; color: #9c27b0;');
        console.log('  %cdebug.version.analytics.inject()', 'font-weight: bold', '- Inject test data (5 users, realistic distribution)');
        console.log('  %cdebug.version.analytics.generate({userCount: 10})', 'font-weight: bold', '- Generate custom test data');
        console.log('  %cdebug.version.analytics.clear()', 'font-weight: bold', '- Clear test data');
        console.log('');
        console.log('%c🔧 Utility:', 'font-weight: bold; color: #6c5ce7;');
        console.log('  %cdebug.resetFlags()', 'font-weight: bold', '- Reset all debug flags to default');
        console.log('  %cdebug.help()', 'font-weight: bold', '- Show this help message');
        console.groupEnd();
      };
      
      // Show a helpful message on page load
      console.log('%c🧪 Debug utilities available!', 'color: #00b894; font-weight: bold; font-size: 14px;', 'Type debug.help() to see available commands');
    }
  }, []); // Empty deps = only run once on mount

  const refreshToken = async (): Promise<string | null> => {
    // DEBUG: Check if we should force a failure (for testing)
    if (typeof window !== 'undefined') {
      if (isDebugEnabled()) {
        console.log('🧪 DEBUG: Checking refresh flags:', {
          forceRefreshFailure: (window as any)._forceRefreshFailure,
          forceInteractionRequired: (window as any)._forceInteractionRequired
        });
      }
      
      if ((window as any)._forceRefreshFailure) {
        console.error('🧪 DEBUG: Forcing token refresh failure (simulated)');
        (window as any)._forceRefreshFailure = false; // Reset flag
        const error = new Error('DEBUG: Simulated token refresh failure');
        (error as any).errorCode = 'simulated_failure';
        throw error;
      }
      
      if ((window as any)._forceInteractionRequired) {
        console.error('🧪 DEBUG: Forcing interaction_required error (simulated)');
        (window as any)._forceInteractionRequired = false; // Reset flag
        const error = new Error('User interaction is required');
        (error as any).errorCode = 'interaction_required';
        throw error;
      }
    }
    
    if(isNative) {
      try {
        const accessToken = await acquireToken(`api://${appConfig.entraID.authServerName}/${appConfig.entraID.userImpersonationScope}`);
        setAccessToken(accessToken);
        setIsAuthenticated(true);
        
        appInsights?.trackEvent({
          name: 'TokenRefresh_Manual_Success',
          properties: {
            type: 'native',
            timestamp: new Date().toISOString()
          }
        });
        
        // Return the new token
        return accessToken;
      } catch (error) {
        console.error(">>> Native token acquisition failed", error);
        
        appInsights?.trackException({
          exception: error instanceof Error ? error : new Error(String(error)),
          properties: {
            context: 'TokenRefresh_Manual_Native',
            timestamp: new Date().toISOString()
          }
        });
        throw error;
      }
    }

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];
      
      try {
        const tokenResponse: AuthenticationResult = await msalInstance.acquireTokenSilent({
          account,
          scopes: [
            `api://${appConfig.entraID.authServerName}/${appConfig.entraID.userImpersonationScope}`,
            "User.Read"
          ],
          forceRefresh: true
        });
        
        const timeUntilExpiry = tokenResponse.expiresOn ? 
          Math.round((tokenResponse.expiresOn.getTime() - Date.now()) / 1000 / 60) : null;
        
        if (isDebugEnabled()) {
          console.log(">>> Main refreshToken: Token refreshed", {
            success: true,
            isNewToken: tokenResponse.accessToken !== tokenRef.current,
            oldExpiry: tokenRef.current ? decodeJwtExpiration(tokenRef.current) : null,
            newExpiry: decodeJwtExpiration(tokenResponse.accessToken),
            timeUntilExpiry: timeUntilExpiry ? `${timeUntilExpiry} minutes` : 'unknown'
          });
        }
        
        setAccessToken(tokenResponse.accessToken);
        setUser(account);
        setIsAuthenticated(true);
        
        appInsights?.trackEvent({
          name: 'TokenRefresh_Manual_Success',
          properties: {
            type: 'msal',
            timeUntilExpiry: timeUntilExpiry?.toString(),
            timestamp: new Date().toISOString()
          }
        });
        
        // Return the new token
        return tokenResponse.accessToken;
      } catch (error) {
        console.error(">>> MSAL silent token acquisition failed", {
          error: error instanceof Error ? error.message : String(error),
          errorType: error instanceof Error ? error.constructor.name : typeof error,
          timestamp: new Date().toISOString()
        });
        
        appInsights?.trackException({
          exception: error instanceof Error ? error : new Error(String(error)),
          properties: {
            context: 'TokenRefresh_Manual_MSAL',
            errorType: error instanceof Error ? error.constructor.name : typeof error,
            timestamp: new Date().toISOString()
          }
        });
        throw error;
      }
    } else {
      const error = new Error("No MSAL accounts available");
      console.error(">>> No MSAL accounts available for token refresh");
      
      appInsights?.trackException({
        exception: error,
        properties: {
          context: 'TokenRefresh_Manual_NoAccounts',
          timestamp: new Date().toISOString()
        }
      });
      throw error;
    }
  };

  if (!isInitialized) {
    return <Loader />;
  }

  return (
    <EntraIdContext.Provider
      value={{ 
        isAuthenticated, 
        user, 
        accessToken, 
        getCurrentToken, 
        login, 
        logout, 
        refreshToken
      }}
    >
      {children}
    </EntraIdContext.Provider>
  );
};

export default EntraIdContext;